import React from 'react';
import { Snackbar, IconButton, Slide, Box } from '@mui/material';
import { Close, CheckCircleOutline, ErrorOutline, InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Slide transition from right edge
const SlideTransition = React.forwardRef((props, ref) => {
    return <Slide {...props} ref={ref} direction="left" />;
});

/**
 * Modern Custom Snackbar Component
 * Slides in from the right edge of the screen
 * Right side flush with browser, left side rounded
 * Supports light and dark mode with smooth animations
 * 
 * @param {boolean} open - Whether the snackbar is visible
 * @param {function} onClose - Callback when snackbar closes
 * @param {string} message - Message to display
 * @param {string} severity - 'success' | 'error' | 'warning' | 'info'
 * @param {number} autoHideDuration - Auto hide duration in ms (default: 4000)
 * @param {boolean} showCopyAction - Whether to show copy button (for links)
 * @param {function} onCopy - Callback when copy button is clicked
 */
const CustomSnackbar = ({ 
    open, 
    onClose, 
    message, 
    severity = 'info', 
    autoHideDuration = 4000,
    showCopyAction = false,
    onCopy,
    sx = {}
}) => {
    const { isDarkMode } = useAppTheme();

    const getIcon = () => {
        switch (severity) {
            case 'success':
                return <CheckCircleOutline />;
            case 'error':
                return <ErrorOutline />;
            case 'warning':
                return <WarningAmberOutlined />;
            default:
                return <InfoOutlined />;
        }
    };

    const getSeverityColors = () => {
        if (isDarkMode) {
            switch (severity) {
                case 'success':
                    return {
                        // Dark emerald/teal background with bright mint accents
                        bg: 'linear-gradient(135deg, #0d2818 0%, #1a3d2e 100%)',
                        accent: '#10b981',
                        iconBg: 'rgba(16, 185, 129, 0.15)',
                        text: '#ecfdf5',
                        iconColor: '#34d399'
                    };
                case 'error':
                    return {
                        // Deep dark red/burgundy with coral accents
                        bg: 'linear-gradient(135deg, #1f0a0a 0%, #3b1212 100%)',
                        accent: '#f87171',
                        iconBg: 'rgba(248, 113, 113, 0.15)',
                        text: '#fef2f2',
                        iconColor: '#fca5a5'
                    };
                case 'warning':
                    return {
                        // Dark amber/bronze with golden accents
                        bg: 'linear-gradient(135deg, #1c1206 0%, #362309 100%)',
                        accent: '#f59e0b',
                        iconBg: 'rgba(245, 158, 11, 0.15)',
                        text: '#fefce8',
                        iconColor: '#fbbf24'
                    };
                default:
                    return {
                        // Deep navy/indigo with cyan accents
                        bg: 'linear-gradient(135deg, #0a1628 0%, #132240 100%)',
                        accent: '#0ea5e9',
                        iconBg: 'rgba(14, 165, 233, 0.15)',
                        text: '#f0f9ff',
                        iconColor: '#38bdf8'
                    };
            }
        } else {
            switch (severity) {
                case 'success':
                    return {
                        bg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(232, 245, 233, 0.98) 100%)',
                        accent: '#4caf50',
                        iconBg: 'rgba(76, 175, 80, 0.12)',
                        text: '#1b5e20',
                        iconColor: '#2e7d32'
                    };
                case 'error':
                    return {
                        bg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 235, 238, 0.98) 100%)',
                        accent: '#f44336',
                        iconBg: 'rgba(244, 67, 54, 0.12)',
                        text: '#c62828',
                        iconColor: '#d32f2f'
                    };
                case 'warning':
                    return {
                        bg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 243, 224, 0.98) 100%)',
                        accent: '#ff9800',
                        iconBg: 'rgba(255, 152, 0, 0.12)',
                        text: '#e65100',
                        iconColor: '#f57c00'
                    };
                default:
                    return {
                        bg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(227, 242, 253, 0.98) 100%)',
                        accent: '#03a9f4',
                        iconBg: 'rgba(3, 169, 244, 0.12)',
                        text: '#01579b',
                        iconColor: '#0277bd'
                    };
            }
        }
    };

    const severityColors = getSeverityColors();

    // eslint-disable-next-line
    const handleCopy = async () => {
        if (onCopy) {
            onCopy();
        }
        if (message) {
            // Try modern clipboard API first, fallback to execCommand
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(message);
                } else {
                    // Fallback for non-secure contexts (HTTP)
                    const textArea = document.createElement('textarea');
                    textArea.value = message;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    textArea.style.top = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                }
            } catch (err) {
                console.error('Failed to copy to clipboard:', err);
            }
        }
        if (onClose) {
            onClose();
        }
    };

    return (
        <Snackbar
            open={open}
            onClose={onClose}
            autoHideDuration={autoHideDuration}
            TransitionComponent={SlideTransition}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
                top: '80px !important',
                right: '0 !important',
                left: 'auto !important',
                transform: 'none !important',
                '& .MuiSnackbarContent-root': {
                    padding: 0,
                    minWidth: 'auto'
                },
                ...sx
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    pl: 2.5,
                    pr: 2,
                    py: 1.5,
                    minWidth: 320,
                    maxWidth: 420,
                    // Left side rounded, right side flush
                    borderRadius: '16px 0 0 16px',
                    background: severityColors.bg,
                    // Left accent bar
                    borderLeft: `4px solid ${severityColors.accent}`,
                    backdropFilter: 'blur(20px)',
                    boxShadow: isDarkMode 
                        ? '-8px 4px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset'
                        : '-8px 4px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                    position: 'relative',
                    overflow: 'hidden',
                    // Subtle shine effect
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: isDarkMode 
                            ? 'linear-gradient(90deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)'
                            : 'linear-gradient(90deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.2) 100%)',
                    }
                }}
            >
                {/* Icon Container */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        bgcolor: severityColors.iconBg,
                        color: severityColors.iconColor,
                        flexShrink: 0,
                        boxShadow: `0 2px 8px ${severityColors.accent}25`,
                        '& svg': {
                            fontSize: 22
                        }
                    }}
                >
                    {getIcon()}
                </Box>

                {/* Message */}
                <Box
                    sx={{
                        flex: 1,
                        color: severityColors.text,
                        fontSize: '0.925rem',
                        fontWeight: 600,
                        lineHeight: 1.5,
                        letterSpacing: '-0.01em',
                        wordBreak: 'break-word',
                        pr: 1
                    }}
                >
                    {message}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{
                            color: severityColors.text,
                            opacity: 0.6,
                            width: 32,
                            height: 32,
                            '&:hover': {
                                opacity: 1,
                                bgcolor: isDarkMode 
                                    ? 'rgba(255, 255, 255, 0.15)' 
                                    : 'rgba(0, 0, 0, 0.06)',
                                transform: 'rotate(90deg)'
                            },
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>
        </Snackbar>
    );
};

export default CustomSnackbar;
